/**
 * Zugriff auf die Regeltexte.
 *
 * Zwei Quellen, beide gemeinfrei (US Government Work):
 * - `navrules`: die amalgamierte Fassung der US Coast Guard. Sie führt die
 *   internationalen Regeln (72 COLREGS) und die US-Binnenregeln in einem Text und
 *   markiert jede Abweichung. Das ist die Quelle, aus der die App zitiert.
 * - `33cfr83`: die US-Binnenregeln als Bundesrecht. Sie dient der genauen
 *   Rechtsfundstelle (33 CFR 83.x) und bleibt für den amerikanischen Markt drin.
 *
 * Der IMO-Originaltext der COLREGs ist urheberrechtlich geschützt und wird nicht benutzt.
 */
import cfr from '../../data/sources/33cfr83.json'
import navrulesRaw from '../../data/sources/navrules.json'
import cfr62Raw from '../../data/sources/33cfr62.json'
import seeAnlage1Raw from '../../data/sources/seeschstro-anlage1.json'
import binschstroRaw from '../../data/sources/binschstro.json'

export type Scope = 'both' | 'international' | 'inland'

export interface SourceParagraph {
  id: string
  text: string
}

export interface SourceSection {
  rule: number
  heading: string
  paragraphs: SourceParagraph[]
}

export interface CfrSource {
  id: string
  title: string
  edition: string
  retrieved: string
  url: string
  license: string
  sections: Record<string, SourceSection>
}

export interface RuleParagraph {
  id: string
  scope: Scope
  international: string
  inland: string
}

export interface Rule {
  number: number
  title: string
  /** Anhänge tragen ihren Namen, weil "Regel 103" niemandem etwas sagt. */
  label?: string
  paragraphs: RuleParagraph[]
}

export interface NavRulesSource {
  id: string
  title: string
  url: string
  retrieved: string
  license: string
  rules: Record<string, Rule>
}

export const cfr83: CfrSource = cfr as CfrSource
export const navrules: NavRulesSource = navrulesRaw as NavRulesSource

export function section(cfrRef: string): SourceSection {
  const s = cfr83.sections[cfrRef]
  if (!s) throw new Error(`Abschnitt ${cfrRef} gibt es in ${cfr83.id} nicht`)
  return s
}

export function paragraph(cfrRef: string, id: string): SourceParagraph {
  const p = section(cfrRef).paragraphs.find((x) => x.id === id)
  if (!p) throw new Error(`Absatz ${cfrRef}(${id}) gibt es nicht`)
  return p
}

export function quote(cfrRef: string, cite: string[]): SourceParagraph[] {
  return cite.map((id) => paragraph(cfrRef, id))
}

export function citation(cfrRef: string): string {
  return `33 CFR ${cfrRef} (${cfr83.edition})`
}

export interface BuoySourceSection {
  id: string
  heading: string
  paragraphs: { id: string; text: string }[]
}

export interface BuoySource {
  id: string
  title: string
  region: 'A' | 'B'
  url: string
  retrieved: string
  license: string
  sections: Record<string, BuoySourceSection>
}

/** Region B: United States Aids to Navigation System. */
export const cfr62: BuoySource = cfr62Raw as BuoySource
/** Region A: Anlage I der Seeschifffahrtsstraßen-Ordnung. */
export const seeAnlage1: BuoySource = seeAnlage1Raw as BuoySource

/**
 * Die Binnenschifffahrtsstraßen-Ordnung, Kapitel 3 (Bezeichnung der Fahrzeuge).
 *
 * Eigene Quelle und eigener Aufbau: die Ordnung zählt nicht in Regeln, sondern in
 * Paragraphen mit Nummern wie "3.08", darunter Nummern und Buchstaben. Sie steht neben
 * den Kollisionsverhütungsregeln und nicht in ihnen; wer die beiden vermischt, bringt
 * jemandem bei, auf dem Rhein nach Seerecht zu fahren.
 */
export interface BinnenTeil {
  id: string
  text: string
}

export interface BinnenAbsatz {
  id: string
  text: string
  teile: BinnenTeil[]
}

export interface BinnenParagraph {
  nummer: string
  titel: string
  bilder: string[]
  absaetze: BinnenAbsatz[]
  /** § 3.06 ist in der Ordnung ohne Inhalt; die Nummer bleibt trotzdem vergeben. */
  leer?: boolean
}

/** Ein Eintrag der Anlage 6, so wie ihn die Tabelle führt (siehe scripts/fetch-binschstro.ts). */
export interface Anlage6Eintrag {
  abschnitt: string
  lage?: string
  fall?: string
  beschreibung: string
  bedeutung?: string
  wer?: string
  fundstelle?: string
}

export interface BinnenQuelle {
  id: string
  title: string
  url: string
  retrieved: string
  license: string
  paragraphs: Record<string, BinnenParagraph>
  anlage6: {
    titel: string
    vorbemerkung: string
    abschnitte: { buchstabe: string; titel: string }[]
    eintraege: Anlage6Eintrag[]
  }
}

export const binschstro: BinnenQuelle = binschstroRaw as BinnenQuelle

/** Ein Paragraph der Ordnung, oder ein Fehler mit Namen. */
export function binnenParagraph(nummer: string): BinnenParagraph {
  const p = binschstro.paragraphs[nummer]
  if (!p) throw new Error(`§ ${nummer} gibt es in ${binschstro.id} nicht`)
  return p
}

/**
 * Text zu einer Fundstelle der Ordnung. Geschrieben wird sie wie im Gesetz zitiert:
 * "3.08:1" ist § 3.08 Nummer 1, "3.08:1a" derselbe Punkt Buchstabe a.
 */
export function binnenText(stelle: string): string {
  const [nummer, rest] = stelle.split(':')
  if (!nummer || !rest) throw new Error(`Fundstelle "${stelle}" ist keine`)
  const absatzId = /^\d+/.exec(rest)?.[0]
  const teilId = rest.slice(absatzId?.length ?? 0)
  const absatz = binnenParagraph(nummer).absaetze.find((a) => a.id === absatzId)
  if (!absatz) throw new Error(`§ ${nummer} hat keine Nummer ${absatzId}`)
  if (!teilId) return absatz.text
  const teil = absatz.teile.find((t) => t.id === teilId)
  if (!teil) throw new Error(`§ ${nummer} Nummer ${absatzId} hat keinen Buchstaben ${teilId}`)
  return teil.text
}

/** Wie die Fundstelle auf dem Bildschirm steht. */
export function binnenZitat(stelle: string): string {
  const [nummer, rest] = stelle.split(':')
  const absatzId = /^\d+/.exec(rest ?? '')?.[0]
  const teilId = (rest ?? '').slice(absatzId?.length ?? 0)
  const teil = teilId ? ` Buchstabe ${teilId}` : ''
  return `BinSchStrO § ${nummer} Nummer ${absatzId}${teil}`
}

/** Text einer Fundstelle in einer der beiden Seezeichen-Quellen. */
export function buoyText(source: BuoySource, section: string, ids: string[]): string[] {
  const sec = source.sections[section]
  if (!sec) throw new Error(`${source.id}: Abschnitt ${section} gibt es nicht`)
  return ids.map((id) => {
    const hits = sec.paragraphs.filter((p) => p.id === id)
    if (hits.length === 0) throw new Error(`${source.id}: ${section} ${id} gibt es nicht`)
    if (hits.length > 1) throw new Error(`${source.id}: ${section} ${id} ist mehrdeutig`)
    return hits[0]!.text
  })
}

export function rule(number: number): Rule {
  const r = navrules.rules[String(number)]
  if (!r) throw new Error(`Regel ${number} gibt es in ${navrules.id} nicht`)
  return r
}

/** Gilt der Absatz für die gefragte Geltung? "both" passt auf alles. */
function applies(p: RuleParagraph, scope: Scope): boolean {
  if (scope === 'both') return true
  return p.scope === 'both' || p.scope === scope
}

/**
 * Der zitierte Absatz. Wirft, wenn es ihn nicht gibt oder wenn er mehrdeutig ist:
 * eine Fundstelle, die zwei Texte treffen kann, ist keine Fundstelle.
 */
export function rulePara(ruleNumber: number, id: string, scope: Scope = 'international'): RuleParagraph {
  const hits = rule(ruleNumber).paragraphs.filter((p) => p.id === id && applies(p, scope))
  if (hits.length === 0) throw new Error(`Regel ${ruleNumber}(${id}) gibt es für "${scope}" nicht`)
  if (hits.length > 1) {
    throw new Error(`Regel ${ruleNumber}(${id}) ist für "${scope}" mehrdeutig: ${hits.length} Absätze`)
  }
  return hits[0]!
}

/** Text eines Absatzes in der gefragten Fassung. */
export function ruleText(p: RuleParagraph, scope: Exclude<Scope, 'both'> = 'international'): string {
  return scope === 'inland' ? p.inland : p.international
}

/** Weicht die Inland-Fassung vom internationalen Text ab? */
export function differs(p: RuleParagraph): boolean {
  if (p.scope !== 'both') return true
  return p.international.trim() !== p.inland.trim()
}

/**
 * Absätze einer Regel, in denen die US-Binnenregeln vom internationalen Text abweichen.
 * Das ist der Unterschied, den kein Lehrbuch der Zielgruppe sauber zeigt.
 */
export function ruleDifferences(ruleNumber: number): RuleParagraph[] {
  return rule(ruleNumber).paragraphs.filter((p) => differs(p))
}

export interface ResolvedCite {
  rule: number
  id: string
  paragraph: RuleParagraph
}

/**
 * Eine Fundstelle, geschrieben als "a" (eigene Regel) oder "23:a" (andere Regel).
 * Der Querverweis ist keine Bequemlichkeit: Regel 28 etwa schreibt drei rote Lichter
 * vor "zusätzlich zu den Lichtern nach Regel 23(a)", und wer nur Regel 28 zitiert,
 * lässt fünf der acht gezeigten Lichter unbelegt.
 */
export function resolveCite(ownRule: number, entry: string, scope: Scope = 'international'): ResolvedCite {
  const match = entry.match(/^(\d+):(.+)$/)
  const ruleNumber = match ? Number(match[1]) : ownRule
  const id = match ? match[2]! : entry
  return { rule: ruleNumber, id, paragraph: rulePara(ruleNumber, id, scope) }
}

/** "c(ii)" in seine Teile: ["c", "ii"], damit die Fundstelle wie im Regelbuch aussieht. */
function split(id: string): string[] {
  return id.split(/[()]+/).filter((piece) => piece.length > 0)
}

/** "Rule 34" oder, bei den Anhängen, "Annex III". */
export function ruleName(ruleNumber: number): string {
  return rule(ruleNumber).label ?? `Rule ${ruleNumber}`
}

/**
 * Die Fundstelle, wie sie in der App steht. Ein Querverweis trägt die Nummer der
 * anderen Regel vor dem Doppelpunkt ("23:a"), ein angehängter Block oder ein
 * Abschnitt eines Anhangs dagegen einen Buchstaben ("b1:b", "s1:b(i)").
 */
export function ruleCitation(ruleNumber: number, cite: string[]): string {
  const foreignCite = (c: string): boolean => /^\d+:/.test(c)
  const own = cite.filter((c) => !foreignCite(c))
  const foreign = [...new Set(cite.filter(foreignCite).map((c) => Number(c.split(':')[0])))]
  const kept = own
    .filter((id) => id !== 'intro')
    // "34(c)" und "34(c)(i)" nebeneinander zitiert sich doppelt: der Oberabsatz steckt
    // im Unterabsatz schon drin.
    .filter((id) => !own.some((other) => other !== id && other.startsWith(`${id}(`)))
  let last: string[] = []
  const ids = kept
    .map((id) => {
      const block = id.match(/^([bs])(\d+):(.+)$/)
      const rest = block ? block[3]! : id
      const pieces = split(rest)
      // Teilen sich zwei Fundstellen den Oberabsatz, steht er nur einmal da:
      // "26(b)(i)(ii)(iii)" statt "26(b)(i)(b)(ii)(b)(iii)".
      const common = pieces.findIndex((piece, i) => last[i] !== piece)
      const shown = common === -1 ? pieces.slice(-1) : pieces.slice(common)
      last = pieces
      const tail = shown.map((piece) => `(${piece})`).join('')
      if (!block) return tail
      return block[1] === 's' ? `, Section ${block[2]}${tail}` : `, ${block[2]}${tail}`
    })
    .join('')
  const base = `COLREG ${ruleName(ruleNumber)}${ids}`
  return foreign.length > 0
    ? `${base} mit ${foreign.sort((a, b) => a - b).map(ruleName).join(', ')}`
    : base
}
